import React from 'react';
import moment from 'moment';

import { ChatActions } from '../components/ChatActions.jsx';
import { Request } from '../components/Request';
import { CacheableDataObject } from 'models/CacheableDataObject.jsx';

import { ChatMessage, ChatMessageNotification } from 'models/ChatMessage.jsx';
import { TokensManager } from '../components/TokensManager';
import { Dashboard } from '../components/Dashboard';
import { BrowserNotification } from 'components/BrowserNotification.jsx';
import { MapHelper } from '../functions/MapHelper';
import { Match } from './Match';
import { privateChatMessageHolder } from 'components/PrivateChatMessageHolder.jsx';

import { Users } from 'models/UserCollection.jsx';

import store from 'react/redux/store/store';
import * as chatActions from 'react/redux/actions/chat';
import { showToast, dismissToast } from "react/toasts/showToast";
import MentionToast from "react/chat/MentionToast";
import { plainRoomLinkText } from "components/MessageParser";
import StateHelpers from "../react/redux/store/StateHelpers";
import { ChatRoom } from "./ChatRoom";
import { scheduleRoomRead } from 'models/chat/RoomReadSync';
import { connectCodeFieldName, findConnectCode, isDismissedConnectCode, myConnectCode, usesConnectCodes } from 'components/connectCode';

class ChatMessages extends CacheableDataObject {

	resetMessages() {
		this.messages = new Map();
	}

	beforeConstruct() {
		this.messages = new Map();
		this.unreadMessages = new Map();
		this.unreadMentions = new Map();
		// The server's read cursor (chat/my_chats read_state, chat_room_read pushes, and our
		// own reads); null when the room has none, which counts as read.
		this.lastReadMessageId = null;
		// read_state's unread count: the badge until the room's messages load.
		this.seededUnreadCount = 0;
		this.loadedHistory = false;
		this.extraMessagesToInject = null;
		this.resetMessagesOnNextInitialLoad = false;

		this.stateMessages = new Map();
		this.stateMessageTimeouts = new Map();

		this.lockedToBottom = true;
		this.completedInitialLoad = false;
		this.initialLoadFailed = false;
		// Counts first-load attempts so a request abandoned by a retry can't settle the load.
		this.initialLoadAttempt = 0;


		this.deleteHighlight = null;
		this.highlightedPlayer = null;
		// The message opened from a mention (focusMessage): highlighted, and scrolled to once.
		this.focusedMessageId = null;
		this.scrollToFocusedMessage = false;
		this.focusTimeout = null;
		this.initialMessagesAdded = Promise.resolve();

		this.latestTime = moment.unix(0);
		this.latestMessage = null;
		// send_id → Slippi code that your message in this room offers to save (this tab only)
		this.connectCodeOffers = new Map();
		this.chatRoom = null;
		this.privateChatBox = null;
		this.match = null;

		this.keepScrollPosition = false;

		this.closed = false;

		this.chatContainerReference = null;
		this.chatInputReference = null;
		this.chatHolderComponent = null;

		this.initialMessageLoad = null;
		this.currentlyEditingMessage = null;

		this.scrollingChatHolderComponents = new Map();

		this.scrollToBottomOnce = true;

		this.hasNewMessagesCallback = () => {
		};
	}

	addScrollingChatHolderComponent(component) {
		this.scrollingChatHolderComponents.set(component, component);
	}

	removeScrollingChatHolderComponent(component) {
		this.scrollingChatHolderComponents.delete(component);
	}

	setMatch(match) {
		this.match = match;
	}

	getLastMessage() {
		if(!this.messages.size) {
			return null;
		}
		return Array.from(this.messages.values()).pop();
	}

	get player() {
		if(this.privateChatBox) {
			return this.privateChatBox.player;
		}
		return null;
	}

	get matchId() {
		if(this.match) {
			return this.match.id;
		}
		return null;
	}

	get chatRoomId() {
		if(this.chatRoom) {
			return this.chatRoom.id;
		} else {
			return null;
		}
	}

	setChatHolderComponent(component) {
		if(component === null) {
			// console.error('why is it blank?');
		}

		this.chatHolderComponent = component;
	}


	highlightPlayerByUsername(username) {
		this.highlightedPlayer = Users.retrieveByUsername(username);
		this.updateDisplay();
	}

	removeUsernameHighlight() {
		this.highlightedPlayer = null;
		this.updateDisplay();
	}

	/**
	 * Scrolls the room to a message and highlights it for a few seconds, paging
	 * back through older messages (at most FOCUS_PAGE_LIMIT pages) until it's
	 * loaded. Resolves false when it couldn't be found that far back.
	 */
	async focusMessage(messageId) {
		const id = String(messageId);
		await this.viewActivated();
		if(this.loadingPreviousMessages) {
			await this.loadingPreviousMessages;
		}
		await this.initialMessagesAdded;
		for(let page = 0; !this.messages.has(id) && !this.loadedAll && page < ChatMessages.FOCUS_PAGE_LIMIT; page++) {
			await this.loadPreviousMessages();
		}
		if(!this.messages.has(id)) {
			return false;
		}
		this.focusedMessageId = id;
		this.scrollToFocusedMessage = true;
		this.lockedToBottom = false;
		clearTimeout(this.focusTimeout);
		this.focusTimeout = setTimeout(() => {
			this.focusedMessageId = null;
			this.updateDisplay();
		}, ChatMessages.FOCUS_HIGHLIGHT_MILLISECONDS);
		this.updateDisplay();
		return true;
	}


	hasAllMessages(messages) {
		for(let i in messages) {
			if(!messages.hasOwnProperty(i)) {
				continue;
			}
			const message = messages[i];
			if(!this.messages.has(i)) {
				return false;
			}
		}
		return true;
	}

	/**
	 * Merge an updated reaction set onto an already-displayed message in place.
	 * Used by the socket reaction broadcast so a reaction never replaces the
	 * message (which would wipe its text or re-mark it unread).
	 */
	applyReactions(messageId, reactions) {
		const message = this.fetchMessage(messageId);
		if(!message) {
			return;
		}
		message.reactions = Array.isArray(reactions) ? reactions : [];
		this.updateDisplay();
	}

	/**
	 * Apply a pushed chat_message_kind_updates entry (an LFG card's new status)
	 * to the loaded message it names, without replacing the message.
	 */
	applyKindUpdate(messageId, update) {
		const message = this.fetchMessage(messageId);
		if(!message) {
			return;
		}
		if(message.applyKindUpdate(update)) {
			this.updateDisplay();
		}
	}

	removeMessage(message) {
		const foundMessage = this.messages.get(message.id);
		if(!foundMessage) {
			console.log('did not find message to remove');
			return;
		}
		this.messages.delete(message.id);
		this.updateDisplay();
	}

	addMessages({ messages, messagesAreNew = false, holdChatPositionAfterLoad = false }) {
		const newMessages = [];
		const multipleChatMessages = messages;
		let hasNewMessage = false;

		if(messages === undefined) {
			throw new Error('message: parameter is undefined');
		}

		let newMessage = null;
		for(let i in multipleChatMessages) {
			if(!multipleChatMessages.hasOwnProperty(i)) {
				continue;
			}
			const message = multipleChatMessages[i];
			if(!message.hasOwnProperty('id')) {
				throw new Error('This message is invalid for the reason of not having an id property!');
			}

			if(message instanceof ChatMessage) {
				newMessage = message;
			} else {
				newMessage = ChatMessage.create(message);
			}
			if(newMessage.isNew === null) {
				newMessage.isNew = messagesAreNew;
			}

			if(messagesAreNew && !newMessage.isStateMessage()) {
				hasNewMessage = true;
			}
			newMessages.push(newMessage);

			this.addMessage(newMessage, false);
		}
		if(newMessages.length) {
			if(!messagesAreNew) {
				this.loadedHistory = true;
			}
			this.sortMessages();
			if(holdChatPositionAfterLoad) {
				this.keepScrollPosition = true;
			}

			if(hasNewMessage) {
				this.hasNewMessagesCallback(newMessage);
			}

			this.updateDisplay();

			store.dispatch(chatActions.updateGeneralChatUnreadMessageState());
		}
		return newMessages;
	}


	_addStateMessage(newMessage, triggerStateChange) {
		clearTimeout(this.stateMessageTimeouts.get(newMessage.player));
		if(newMessage.state_message == true) {
			const timeout = setTimeout(() => {
				this.stateMessages.delete(newMessage.player, newMessage);
				this.updateDisplay();
			}, 16000);
			this.stateMessages.set(newMessage.player, newMessage);
			this.stateMessageTimeouts.set(newMessage.player, timeout);
		} else {
			this.stateMessages.delete(newMessage.player, newMessage);
		}
		if(triggerStateChange) {
			this.updateDisplay();
		}
	}

	_messageShouldBeDisplayed(message) {
		if(!message.id || !message.player) {
			return true;
		}
		if(message.is_shadow_muted) {
			if(message.isModOfMessage) {
			} else {
				if(!message.player.isMe()) {
					if(Match.isInMatchWith(message.player) || privateChatMessageHolder.chatWithPlayerIsOpen(message.player.id) || message.player.isFriend()) {

					} else if(myUser.is_shadow_muted || (message.ip_id === myUser.ip_id && !IS_LOCALHOST)) {
					} else {
						console.log('BEING TOLD NO, SO WHAT HAPPENS?');
						return false;
					}
				}
			}
		}
		if(!message.isModOfMessage && myUser.ignore_list[message.player.id]) {
			return false;
		}

		const isMyMessage = message.isMyMessage();
		if(message.is_muted) {
			if(message.isModOfMessage) {
				if(myUser.ignore_list[message.player.id]) {
				} else if(!isMyMessage) {
					return false;
				}
			} else {
				if(!isMyMessage) {
					return false;
				}
			}
		}
		return true;
	}

	/**
	 *
	 * @param newMessage
	 * @param triggerStateChange
	 */
	addMessage(newMessage, triggerStateChange = true) {
		if(!(newMessage instanceof ChatMessage)) {
			console.error(newMessage);
			throw new Error('should be a ChatMessage');
		}

		if(newMessage.isStateMessage()) {
			if(newMessage.player) {
				this._addStateMessage(newMessage, triggerStateChange);
				return;
			} else {
				console.error('have newMessage stateMessgae with no player');
				console.log(newMessage);
				return;
			}
		}

		let previousMessage = null;
		if(newMessage.player && newMessage.player.isMe() && newMessage.send_id && this.fetchMessage('sending_' + newMessage.send_id)) //Update temporary message
		{
			previousMessage = this.fetchMessage('sending_' + newMessage.send_id);
			if(newMessage.id) {
				this.messages.delete('sending_' + newMessage.send_id);
				newMessage.time = previousMessage.time;
				newMessage.listKey = previousMessage.getListKey();
			} else {
				if(!newMessage.time) {
					newMessage.time = this.latestTime.clone().add('1', 'second');
				}
			}
		} else if(!newMessage.id && newMessage.send_id) {
			this.messages.set('sending_' + newMessage.send_id, newMessage);
			if(newMessage.time.isBefore(this.latestTime)) {
				newMessage.time = this.latestTime.clone().add('1', 'second');
			}
		} else {
			const previousMessage = this.fetchMessage(newMessage.id);
			if(previousMessage) {
				if(previousMessage !== newMessage) {
					if(newMessage.deleted && previousMessage.message) {
						newMessage.update({
							message: previousMessage.message
						});
					}
				}
				if(newMessage.player && newMessage.player.isMe()) {
					newMessage.time = previousMessage.time;
					newMessage.listKey = previousMessage.getListKey();
				}
			}
		}


		newMessage.setChatMessages(this);

		if(newMessage.isNotification) {
			newMessage.time = this.latestTime.clone().add('1', 'second');
			if(!newMessage.type) {
				throw new Error('notification needs a type!');
			}
		}

		if(newMessage.time && newMessage.time.isAfter(this.latestTime)) {
			this.latestTime = newMessage.time;
			this.latestMessage = newMessage;
		}


		if(this._messageShouldBeDisplayed(newMessage)) {
			if(newMessage.id) {
				this.messages.set(newMessage.id, newMessage);
				// Your own messages (and their echoes) are never unread.
				const sentByMe = !!newMessage.player && typeof newMessage.player.isMe === 'function' && newMessage.player.isMe();
				if(!sentByMe && this._isUnreadCandidate(newMessage)) {
					if(!StateHelpers.windowHasVisibility() || !this.isActive() || (this.isActive() && !this.lockedToBottom)) {
						this.addUnreadMessage(newMessage);
						this._addStateMessage(newMessage); // Should remove state message in theory to prevent the infinite is typing..
					} else {
						this._readUpTo(Number(newMessage.id));
					}
				}
			}
		} else {
			if(this.messages.has(newMessage.id)) {
				this.messages.delete(newMessage.id);
			}
			this.updateDisplay();
		}

		store.dispatch(chatActions.updateGeneralChatUnreadMessageState());
		if(triggerStateChange) {
			if(!previousMessage) {
				this.sortMessages();
			}
			this.updateDisplay();
		}
	}

	addNotification(message) {
		if(typeof (message) === 'string' || message instanceof String) {
			const notification = ChatMessageNotification.create();
			notification.summary = message;
			message = notification;
		}
		if(!(message instanceof ChatMessageNotification)) {
			throw new Error('must be a chat message notification for now..');
		}
		this.addMessage(message);
	}

	addUnreadMessage(message) {
		if(!message.id) {
			return;
		}
		if(this.chatRoom) {
			if(message.isNew && message.hasMentionFor(myUser)) {

				this.hasNewMentions = true;

				const mentionReason = {};
				mentionReason.titleNotification = 'You were Mentioned!';
				mentionReason.chatNotification = 'mentioned you in';
				const chatRoomName = this.chatRoom.name;
				let toastId = null;
				toastId = showToast({
					user: message.player,
					content: <MentionToast chatRoom={this.chatRoom} message={message} onDone={() => dismissToast(toastId)} />,
				});
				BrowserNotification.showNotification(mentionReason.titleNotification,
					{
						body: message.player.username + ' ' + (mentionReason.chatNotification) + ' ' + chatRoomName + ': ' + plainRoomLinkText(message.message, message.linkedRooms),
						icon:
							message.player.selected_flair ? message.player.selected_flair.fullUrl : undefined,
						onClick: () => {
							window.focus();
							this.chatRoom.setActive();
						},
						// The web push for this mention (RoomMentions) has the same tag, so the two show as one.
						tag: `mention-${message.id}`
					}
				);
				this.unreadMentions.set(message, message);
			}
			if(this.lockedToBottom && this.chatRoom.isActive()) {
				return;
			}
		}
		this.unreadMessages.set(String(message.id), message);
	}


	sortMessages() {
		MapHelper.inPlaceSort(this.messages, ([_, a], [__, b]) => {
			return ChatMessages.compareMessages(a, b);
		});
	}

	hasMod(user) {
		if(this.chatRoom) {
			return this.chatRoom.hasMod(user);
		}
	}

	hasAdmin(user) {
		if(this.chatRoom) {
			return this.chatRoom.hasAdmin(user);
		}
	}

	addMention(player) {
		const input = this.chatInputComponent;
		input.addMention(player);
	}

	allMessagesByPlayer(player) {
		const messages = [];
		for(let [_, message] of this.messages) {
			if(message.player === player) {
				messages.push(message);
			}
		}
		return messages;
	}

	deleteAllByPlayer(player) {
		this.allMessagesByPlayer(player).slice(-50).forEach(function(message) {
			message.deleteMessage();
		});
	}

	removeAllByPlayer(player) {
		this.allMessagesByPlayer(player).slice(-50).forEach(function(message) {
			message.removeMessage();
		});
	}

	addDeleteHighlight(playerOrMessage) {
		this.deleteHighlight = playerOrMessage;
		this.updateDisplay();
	}

	removeDeleteHighlight(player) {
		if((player && this.deleteHighlight === player) || !player) {
			this.deleteHighlight = null;
			this.updateDisplay();
		}
	}

	isActive() {
		if(this.chatRoom) {
			return this.chatRoom.isActive();
		}
		if(this.privateChatBox) {
			return this.privateChatBox.isActive();
		}
		return false;
	}

	changeChatLeft() {
		if(this.chatRoom) {
			return this.chatRoom.changeChatLeft();
		}
		if(this.privateChatBox) {
			return this.privateChatBox.privateChatMessageHolder.changeChatLeft();
		}
	}

	changeChatRight() {
		if(this.chatRoom) {
			return this.chatRoom.changeChatRight();
		}
		if(this.privateChatBox) {
			return this.privateChatBox.privateChatMessageHolder.changeChatRight();
		}
	}

	get chatMessages() {
		return this;
	}

	setChatContainerReference(chatContainerReference) {
		this.chatContainerReference = chatContainerReference;
	}

	setChatInputReference(chatInputReference) {
		this.chatInputReference = chatInputReference;
	}

	setChatInputComponent(chatInputReference) {
		this.chatInputComponent = chatInputReference;
	}

	fetchMessage(messageId) {
		return this.messages.get(String(messageId));
	}

	getChatInput() {
		return this.chatInputReference;
	}

	focusInput() {
		// console.log('input?', this.getChatInput(), this);
		this.getChatInput().focus();
	}

	hasUnreadMessages() {
		return this.unreadCount() > 0;
	}

	/**
	 * Before the room's messages load, read_state's count plus what arrived since.
	 * After, the loaded unread messages, or read_state's count when it counted more
	 * than loaded.
	 */
	unreadCount() {
		if(!this.loadedHistory) {
			return this.seededUnreadCount + this.unreadMessages.size;
		}
		return Math.max(this.seededUnreadCount, this.unreadMessages.size);
	}

	markAllRead() {
		this.hasNewMentions = false;
		this.unreadMessages.clear();
		this.seededUnreadCount = 0;
		if(this.chatRoom && this.isActive() && StateHelpers.windowHasVisibility()) {
			const newestMessageId = this._newestMessageId();
			if(newestMessageId !== null) {
				this._readUpTo(newestMessageId);
			}
		}
	}

	/** Clears every unread message and mention, loaded or only counted; the caller moves the server cursor. */
	markEverythingRead() {
		this.hasNewMentions = false;
		this.unreadMessages.clear();
		this.unreadMentions.clear();
		this.seededUnreadCount = 0;
	}

	/** chat_rooms[id].read_state from chat/my_chats or chat/join. */
	applyReadState(readState) {
		if(!readState) {
			return;
		}
		const serverCursor = readState.last_read_message_id === null || readState.last_read_message_id === undefined
			? null
			: Number(readState.last_read_message_id);
		// We already read past what the server counted from.
		const localCursorIsAhead = this.lastReadMessageId !== null && (serverCursor === null || serverCursor < this.lastReadMessageId);
		this._raiseCursor(serverCursor);
		if(!this.loadedHistory) {
			this.seededUnreadCount = localCursorIsAhead ? 0 : (Number(readState.unread_count) || 0);
		}
		this._clearUnreadUpToCursor();
	}

	/** Another session of this player read the room up to lastReadMessageId (chat_room_read). */
	applyRemoteRead(lastReadMessageId) {
		this._raiseCursor(Number(lastReadMessageId));
		this.seededUnreadCount = 0;
		this._clearUnreadUpToCursor();
	}

	_isUnreadCandidate(message) {
		const id = Number(message.id);
		if(this.lastReadMessageId !== null && Number.isFinite(id)) {
			return id > this.lastReadMessageId;
		}
		return !!message.isNew;
	}

	_raiseCursor(messageId) {
		if(messageId === null || !Number.isFinite(messageId)) {
			return false;
		}
		if(this.lastReadMessageId === null || messageId > this.lastReadMessageId) {
			this.lastReadMessageId = messageId;
			return true;
		}
		return false;
	}

	/** Moves our cursor and the server's (batched; see RoomReadSync) in a room. */
	_readUpTo(messageId) {
		if(!this.chatRoom || !this.chatRoomId) {
			return;
		}
		if(this._raiseCursor(messageId)) {
			scheduleRoomRead(this.chatRoomId, messageId);
		}
	}

	_clearUnreadUpToCursor() {
		if(this.lastReadMessageId === null) {
			return;
		}
		for(const [id] of this.unreadMessages) {
			if(Number(id) <= this.lastReadMessageId) {
				this.unreadMessages.delete(id);
			}
		}
		if(!this.unreadMessages.size) {
			this.hasNewMentions = false;
			this.unreadMentions.clear();
		}
	}

	_newestMessageId() {
		let newest = null;
		for(const [_, message] of this.messages) {
			const id = Number(message.id);
			if(Number.isFinite(id) && (newest === null || id > newest)) {
				newest = id;
			}
		}
		return newest;
	}

	setBrokenMessageState() {
		// TODO: This could potentially set a Marker for last place where messages could be missing in chat, for now we are just going to clear all chat messages
		this.initialMessageLoad = false; //Reset this so that messages will get refreshed
		this.resetMessagesOnNextInitialLoad = true;
	}

	get chatContainer() {
		if(this.chatContainerReference && this.chatContainerReference.current) {
			return this.chatContainerReference.current;
		} else {
		}
	}

	updateDisplay() {
		// if(this.keepScrollPosition)
		// {
		//     var position = new ScrollPosition(this.chatContainer);
		//     position.prepareFor('up');
		// }
		//
		this.scrollingChatHolderComponents.forEach((value, key) => {
			value.forceUpdate();
		});
		//
		// if(this.keepScrollPosition)
		// {
		//     position.restore();
		// }
	}

	onKeyDown(event) {
		console.log('keydown!!');
		console.log(event);
		let chatInput = $(this);
		if(chatInput.data('autocompleteUsed')) {
			event.preventDefault();
			return;
		}
	}

	send(textString, responseId) {
		if(this.isDisabled) {
			return false;
		}

		const sendData = {
			chat_room_id: this.chatRoomId,
			to_user_id: this.player ? this.player.id : null,
			match_id: this.matchId,
			send_id: ChatMessage.generateSendId(),
			message: textString,
			premade_response_id: responseId,
		};

		const chatMessage = ChatMessage.create({
			player: myUser,
			message: textString,
			matchId: this.matchId,
			send_id: sendData.send_id,
			responseId: responseId
		});

		chatMessage.setChatMessages(this);

		if(_.trim(textString).length === 0 && !responseId) {
			return false;
		}

		addGaEvent('matchmaking', 'sendingMessage');

		this.offerConnectCode(sendData.send_id, textString);
		chatMessage.send();

		return true;
	}

	// A connect code in your message in a room on a ladder whose players enter
	// each other's codes (Slippi, Brawl Online), unless it's already your code
	// for that field or you said it isn't yours: the message shows a card
	// offering to save it ({code, type, name}). Match chat applies codes on the
	// server instead.
	offerConnectCode(sendId, textString) {
		const ladder = this.chatRoom?.ladder;
		if(this.match || !ladder || !usesConnectCodes(ladder.player_code_type)) {
			return;
		}
		const type = ladder.player_code_type;
		const code = findConnectCode(textString);
		if(!code || code === myConnectCode(type) || isDismissedConnectCode(code)) {
			return;
		}
		this.connectCodeOffers.set(String(sendId), { code, type, name: connectCodeFieldName(ladder, type) });
	}

	connectCodeOfferFor(message) {
		if(!message?.send_id) {
			return null;
		}
		return this.connectCodeOffers.get(String(message.send_id)) || null;
	}

	/**
	 * "+ LFG": post an LFG card in this room and start its search. The card
	 * shows at once as pending; chat/post_lfg answers with the stored message
	 * (same send_id), which replaces it in place, and the search, which starts
	 * the Play timer. A failure keeps the card with the send-failed state.
	 *
	 * @param options { matchCount (0 = endless), ranked, teamSize, title, main, ladder (rooms without a game only) }
	 */
	postLfg({ matchCount = 0, ranked = false, teamSize = 1, title = '', main = null, ladder = null }) {
		const roomLadder = this.chatRoom && this.chatRoom.ladder ? this.chatRoom.ladder : null;
		const cardLadder = roomLadder || ladder;
		const trimmedTitle = String(title || '').trim();
		let mode = ChatMessage.lfgModes.FRIENDLIES;
		if(teamSize === 2) {
			mode = ChatMessage.lfgModes.DOUBLES;
		} else if(ranked) {
			mode = ChatMessage.lfgModes.RANKED;
		}

		const lfgRequest = {
			chat_room_id: this.chatRoomId,
			match_count: matchCount,
			ranked: ranked ? 1 : 0,
			team_size: teamSize,
		};
		if(trimmedTitle) {
			lfgRequest.title = trimmedTitle;
		}
		if(!roomLadder && ladder) {
			lfgRequest.ladder_id = ladder.id;
		}

		const card = ChatMessage.create({
			player: myUser,
			message: '',
			send_id: ChatMessage.generateSendId(),
			kind: ChatMessage.kinds.LFG,
			kind_data: {
				match_id: null,
				player_id: myUser.id,
				ladder: cardLadder ? { id: cardLadder.id, name: cardLadder.name, slug: cardLadder.slug } : null,
				mode,
				best_of: matchCount ? matchCount : null,
				main,
				title: trimmedTitle || null,
				status: ChatMessage.lfgStatuses.OPEN,
				expires_at: null,
			},
		});
		card.lfgRequest = lfgRequest;
		card.setChatMessages(this);
		card.sendPromise = card.send();
		return card;
	}

	viewActivated() {
		if(this.initialMessageLoad) {
			return Promise.resolve();
		} else {
			this.initialMessageLoad = true;
			return this.getInitialChatMessages();
		}
	}

	async loadPreviousMessages() {
		if(!this.completedInitialLoad) {
			return Promise.reject(new Error('too early to do initial load'));
		}
		const data = {};
		const result = this.messages.values().next();
		const message = result.value;
		if(message) {
			data.top_message_id = message.id;
		}
		if(!this.player && this.messages.size === 0) {
			return Promise.reject(new Error('Invalid container to load new messages'));
		}
		if(this.loadedAll) {
			console.log('already loaded all messages');
			return Promise.resolve();
		}
		if(this.loadingPreviousMessages) {
			return this.loadingPreviousMessages;
		}
		this.loadingPreviousMessages = this.requestPreviousMessages(data)
			.then((response) => {

				this.loadingPreviousMessages = null;
				//TODO: Maybe add a "is_first_message" property to the top message.
				this.keepScrollPosition = true;
				if(this.player) {
					let newMessages = this.parsePrivateChatResponse(response);
				} else {
					if(response.loaded_all) {
						this.loadedAll = true;
					}
					const messages = response.chat_rooms.chat_room[this.chatRoomId].chat_messages;
					if(this.hasAllMessages(messages)) {
						this.loadedAll = true;
					}
					this.addMessages({ messages: messages, holdChatPositionAfterLoad: true });
				}

			}).finally(() => {
				this.loadingPreviousMessages = null;
				this.updateDisplay();
			});
		this.updateDisplay();
		return this.loadingPreviousMessages;
	}

	requestPreviousMessages(data) {
		if(this.player) {
			return Request.apiv1Get('chat/direct_messages', { ...data, other_player_id: this.player.id });
		}
		return Request.apiv1Get('chat/previous_chat_messages', data);
	}

	parsePrivateChatResponse(response) {
		let newMessages = null;
		if(response.private_chat && !response.private_chat_user) {
			const messages = response.private_chat;
			if(messages && messages.chat_messages) {
				newMessages = this.addMessages({ messages: messages.chat_messages });
			}
		} else {
			const messages = response.private_chat_user;
			if(messages.private_chat && messages.private_chat.chat_messages) {
				try {
					newMessages = this.addMessages({ messages: messages.private_chat.chat_messages });
				} catch(e) {
					console.error(e);
				}
			}
		}
		return newMessages;
	}

	/**
	 * Loads the room's first messages from a response already in hand (the
	 * startup payload's chat_messages section) instead of requesting them.
	 */
	loadInitialChatRoomMessagesFrom(response) {
		this.initialMessageLoad = true;
		return this.getInitialChatMessages(response);
	}

	get isLoadingFirstMessages() {
		return !this.completedInitialLoad && !this.initialLoadFailed && !this.match;
	}

	retryInitialLoad() {
		this.loadingPreviousMessages = null;
		this.initialMessageLoad = true;
		const loading = this.getInitialChatMessages();
		this.updateDisplay();
		return loading;
	}

	getInitialChatMessages(preloadedResponse = null) {
		if(this.loadingPreviousMessages) {
			return this.loadingPreviousMessages;
		}

		const attempt = ++this.initialLoadAttempt;
		const isCurrentAttempt = () => attempt === this.initialLoadAttempt;
		this.initialLoadFailed = false;
		const succeeded = () => {
			if(isCurrentAttempt()) {
				this.completedInitialLoad = true;
			}
		};
		const failed = (error) => {
			if(isCurrentAttempt()) {
				this.initialLoadFailed = true;
			}
			throw error;
		};
		const finished = () => {
			if(!isCurrentAttempt()) {
				return;
			}
			this.loadingPreviousMessages = null;
			this.updateDisplay();
		};
		if(this.chatRoomId) {
			const loaded = preloadedResponse
				? Promise.resolve().then(() => this.applyInitialChatRoomMessages(preloadedResponse))
				: this.getInitialChatRoomMessages();
			return this.loadingPreviousMessages = loaded
				.then(succeeded, failed)
				.finally(finished);
		} else if(this.player) {
			return this.loadingPreviousMessages = this.getInitialDirectMessages()
				.then(succeeded, failed)
				.finally(finished);
		} else if(this.match) {
			return Promise.resolve();
		} else {
			throw new Error('missing context to load messages from');
		}
	}

	hasContext() {
		return this.chatRoomId || this.player || this.match;
	}

	getInitialDirectMessages() {
		const data = {};
		data.other_player_id = this.player.id;
		return Request.apiv1Get('chat/direct_messages', data).then((response) => {
			if(!response.private_chat_user) {
				return;
			}
			const privateChatUser = response.private_chat_user;
			const messages = privateChatUser.private_chat;
			delete privateChatUser.private_chat;
			const player = Users.update(response.private_chat_user);

			privateChatMessageHolder.updatePrivateChat({ player: player, blocked: response.blocked, isOpen: true });

			if(messages) {
				this.addMessages({ messages: messages.chat_messages });
			} else {
				this.updateDisplay();
			}
			if(this.privateChatBox) {
				this.privateChatBox.changed();
			}

			return null;
		});
	}

	getInitialChatRoomMessages() {
		const data = {};
		data.chat_room_id = this.chatRoomId;
		return Request.apiv1Get('chat/messages', data)
			.then((response) => this.applyInitialChatRoomMessages(response));
	}

	applyInitialChatRoomMessages(response) {
		if(!response.chat_rooms) {
			return;
		}

		var chatRooms = response.chat_rooms;
		delete response.chat_rooms;

		var timeout = 0;
		var orderedChats = [];

		for(var chatRoom in chatRooms.chat_room) {
			chatRoom = chatRooms.chat_room[chatRoom];
			orderedChats.push(chatRoom);
		}
		orderedChats.sort(function(a, b) {
			if(a.last_active) {
				return -1;
			}
			if(b.last_active) {
				return 1;
			}
			return a.order > b.order ? 1 : -1;
		});

		const added = [];
		_.each(orderedChats, (chat_room) => {
			let markAdded;
			const addedRoom = new Promise((resolve) => { markAdded = resolve; });
			if(String(chat_room.id) === String(this.chatRoomId)) {
				added.push(addedRoom);
			}
			const chatRoomObject = {};
			chatRoomObject[chat_room.id] = chat_room;
			const chatMessages = chat_room.chat_messages;


			setTimeout(() => {
				chatRoomObject[chat_room.id].chat_messages = chatMessages;
				chatRoomObject[chat_room.id].loading_previous_messages = true;


				if(this.resetMessagesOnNextInitialLoad) {
					this.resetMessages();
					this.resetMessagesOnNextInitialLoad = false;
				}
				try {
					Dashboard.performOpenSearchUpdate({ chat_rooms: { chat_room: chatRoomObject } });
				} finally {
					markAdded();
				}
			}, timeout);
			timeout += 1000;
		});
		// The messages go in on timers after this returns; focusMessage waits for this room's.
		this.initialMessagesAdded = Promise.all(added);
	}

	getRecentChattersSorted() {
		const chatters = new Map();
		for(let [_, message] of this.messages) {
			if(message.isNotification || !message.player || message.player.is_system) {
				continue;
			}
			chatters.delete(message.player.id);
			chatters.set(message.player.id, message.player);
		}
		const theChatters = Array.from(chatters.values()).reverse();
		if(theChatters.length > 10) {
			theChatters.length = 10;
		}
		return theChatters;
	}

	shouldShowSlippiName() {
		if(this?.chatRoom?.ladder?.id === 2) {
			return true;
		}
		if(this?.match?.ladder?.id === 2) {
			return true;
		}
		return false;
	}

}

// How far back focusMessage pages for a message, and how long it stays highlighted.
ChatMessages.FOCUS_PAGE_LIMIT = 5;
ChatMessages.FOCUS_HIGHLIGHT_MILLISECONDS = 4000;

ChatMessages.compareMessages = function(message1, message2) { //TO BE DEPRECATED
	if(message1.time.isSame(message2.time)) {
		if(message1.id > message2.id) {
			return 1;
		} else if(message1.id < message2.id) {
			return -1;
		} else {
			return 0;
		}
	}
	if(message1.time.isAfter(message2.time)) {
		return 1;
	}
	if(message1.time.isBefore(message2.time)) {
		return -1;
	}
	return 0;
};


export { ChatMessages };